import {Component, inject, OnInit} from '@angular/core';
import {AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, Validators} from "@angular/forms";
import {BulkActionService} from "../../../service/bulk.action.service";
import {MatDialogRef} from "@angular/material/dialog";
import {BulkActionRequest} from "../../../model/bulk/bulkActionRequest";
import {BulkActionType} from "../../../model/bulk/bulkActionType";
import {NgIf} from "@angular/common";
import {Router} from "@angular/router";
import {MatOption} from "@angular/material/autocomplete";
import {MatSelect} from "@angular/material/select";
import {CdkTextareaAutosize} from "@angular/cdk/text-field";
import {TRACKING_INTERVAL_OPTIONS} from "../../../model/bulk/trackingIntervalOption";
import {GatewayEnvironmentService} from "../../../service/gateway.environment.service";
import {GatewayEnvironment} from "../../../model/gateway/gatewayEnvironment";
import {HttpErrorResponse} from "@angular/common/http";

@Component({
  selector: 'app-bulk-action-create',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    NgIf,
    MatOption,
    MatSelect,
    CdkTextareaAutosize
  ],
  templateUrl: './bulk-action-create.component.html',
  styleUrl: './bulk-action-create.component.css'
})
export class BulkActionCreateComponent implements OnInit {
  private readonly IMEI_REGEX = "30[0-9]{12}0$";
  private readonly UUID_REGEX = "^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$";
  readonly intervalOptions = TRACKING_INTERVAL_OPTIONS;

  private formBuilder = inject(FormBuilder);
  private bulkActionService = inject(BulkActionService);
  private gatewayEnvironmentService = inject(GatewayEnvironmentService);
  private matDialogRef = inject(MatDialogRef<BulkActionCreateComponent>);
  private router = inject(Router);

  bulkActionForm = this.buildForm();
  gatewayEnvironments: GatewayEnvironment[] = [];
  environmentLoadError: string | null = null;
  submitError: string | null = null;

  ngOnInit(): void {
    this.gatewayEnvironmentService.findAllActive().subscribe({
      next: gatewayEnvironments => {
        this.gatewayEnvironments = gatewayEnvironments;

        if (gatewayEnvironments.length > 0) {
          this.gatewayEnvironmentKey?.setValue(gatewayEnvironments[0].environmentKey);
        } else {
          this.gatewayEnvironmentKey?.setValue(null);
          this.environmentLoadError = 'No active gateway environments are configured.';
        }
      },
      error: () => {
        this.gatewayEnvironmentKey?.setValue(null);
        this.environmentLoadError = 'Unable to load gateway environments.';
      }
    });

    this.type?.valueChanges.subscribe(value => {
      if (value === BulkActionType.ASSIGN_DEDICATED_SMS) {
        this.countryCode?.setValidators([Validators.required]);
        this.movingIntervalSeconds?.clearValidators();
        this.stationaryIntervalSeconds?.clearValidators();
        this.gatewayEnvironmentKey?.clearValidators();
        this.movingIntervalSeconds?.setValue(null);
        this.stationaryIntervalSeconds?.setValue(null);
      } else if (value === BulkActionType.UPDATE_TRACKING_INTERVAL) {
        this.countryCode?.clearValidators();
        this.countryCode?.setValue(null);
        this.movingIntervalSeconds?.setValidators([Validators.required]);
        this.stationaryIntervalSeconds?.setValidators([Validators.required]);
        this.gatewayEnvironmentKey?.clearValidators();
      } else if (this.isGatewayBulkAction(value)) {
        this.countryCode?.clearValidators();
        this.countryCode?.setValue(null);
        this.movingIntervalSeconds?.clearValidators();
        this.stationaryIntervalSeconds?.clearValidators();
        this.movingIntervalSeconds?.setValue(null);
        this.stationaryIntervalSeconds?.setValue(null);
        this.gatewayEnvironmentKey?.setValidators([Validators.required]);
      } else {
        this.countryCode?.clearValidators();
        this.countryCode?.setValue(null);
        this.movingIntervalSeconds?.clearValidators();
        this.stationaryIntervalSeconds?.clearValidators();
        this.gatewayEnvironmentKey?.clearValidators();
        this.movingIntervalSeconds?.setValue(null);
        this.stationaryIntervalSeconds?.setValue(null);
      }
      this.countryCode?.updateValueAndValidity();
      this.movingIntervalSeconds?.updateValueAndValidity();
      this.stationaryIntervalSeconds?.updateValueAndValidity();
      this.gatewayEnvironmentKey?.updateValueAndValidity();
      this.bulkActionForm.updateValueAndValidity();
    });
  }

  submit() {
    this.submitError = null;
    const selectedType = this.type?.value as BulkActionType | null;

    const request = new BulkActionRequest(
      this.name?.value!,
      this.splitImeis(),
      selectedType!,
      this.countryCode?.value ?? null,
      this.movingIntervalSeconds?.value ?? null,
      this.stationaryIntervalSeconds?.value ?? null,
      this.isGatewayBulkAction(selectedType) ? this.gatewayEnvironmentKey?.value ?? null : null,
      selectedType === BulkActionType.MANAGE_GATEWAY_DEVICES ? this.normalizeOptionalValue(this.tenantId?.value) : null,
      selectedType === BulkActionType.MANAGE_GATEWAY_DEVICES ? this.splitConnectorIds() : [],
      selectedType === BulkActionType.MANAGE_GATEWAY_DEVICES ? this.normalizeOptionalValue(this.groupId?.value) : null,
    );

    this.bulkActionService.createBulkAction(request).subscribe({
      next: () => {
        this.matDialogRef.close();
        void this.router.navigate(['bulk-actions']);
      },
      error: err => {
        if (err instanceof HttpErrorResponse && typeof err.error === 'string') {
          this.submitError = err.error;
        } else {
          this.submitError = 'Unable to create bulk action.';
        }
      }
    });
  }

  prevent(event: any) {
    event.preventDefault();
  }

  cancel() {
    this.matDialogRef.close();
  }

  private buildForm() {
    const form = this.formBuilder.group({
      name: [null, [Validators.required]],
      imeis: [null, Validators.required],
      type: [null, [Validators.required]],
      countryCode: [null],
      movingIntervalSeconds: [null],
      stationaryIntervalSeconds: [null],
      gatewayEnvironmentKey: [null as string | null],
      tenantId: [null as string | null],
      connectorIds: [null as string | null],
      groupId: [null as string | null],
    }, { validators: [this.validateGatewayFields.bind(this)] });

    form.valueChanges.subscribe(() => {
      this.validateImeis();
    });

    return form;
  }

  private validateGatewayFields(control: AbstractControl): ValidationErrors | null {
    if (control.get('type')?.value !== BulkActionType.MANAGE_GATEWAY_DEVICES) {
      return null;
    }

    const tenantId = this.normalizeOptionalValue(control.get('tenantId')?.value);
    const connectorIds = this.splitValues(control.get('connectorIds')?.value);
    const groupId = this.normalizeOptionalValue(control.get('groupId')?.value);

    if (!tenantId && connectorIds.length === 0 && !groupId) {
      return {'gatewayTargetRequired': true};
    }

    if (tenantId && !tenantId.match(this.UUID_REGEX)) {
      return {'invalidTenantId': true};
    }

    if (connectorIds.some(connectorId => !connectorId.match(this.UUID_REGEX))) {
      return {'invalidConnectorIds': true};
    }

    if (groupId && !groupId.match(this.UUID_REGEX)) {
      return {'invalidGroupId': true};
    }

    return null;
  }

  private validateImeis() {
    const imeis = this.splitImeis();

    if (imeis.length > 0) {
      if (imeis.every(imei => imei.match(this.IMEI_REGEX))) {
        this.imeis?.setErrors(null);
      } else {
        this.imeis?.setErrors({'invalidImei': true});
      }
    }
  }

  private splitImeis(): string[] {
    return this.splitValues(this.imeis?.value);
  }

  private splitConnectorIds(): string[] {
    return this.splitValues(this.connectorIds?.value);
  }

  private splitValues(value: string | null | undefined): string[] {
    if (!value) {
      return [];
    }

    try {
      return value.split(/[\n\s\t,]/).filter((item) => item && item.length > 0);
    } catch (e) {
      console.log(e);
      return [];
    }
  }

  private normalizeOptionalValue(value: string | null | undefined): string | null {
    if (!value || value.trim().length === 0) {
      return null;
    }

    return value.trim();
  }

  isGatewayBulkAction(value: BulkActionType | string | null | undefined): boolean {
    return value === BulkActionType.MANAGE_GATEWAY_DEVICES || value === BulkActionType.GATEWAY_DELETE_DEVICES;
  }

  get name() {
    return this.bulkActionForm.get('name');
  }

  get imeis() {
    return this.bulkActionForm.get('imeis');
  }

  get type() {
    return this.bulkActionForm.get('type');
  }

  get countryCode() {
    return this.bulkActionForm.get('countryCode');
  }

  get movingIntervalSeconds() {
    return this.bulkActionForm.get('movingIntervalSeconds');
  }

  get stationaryIntervalSeconds() {
    return this.bulkActionForm.get('stationaryIntervalSeconds');
  }

  get gatewayEnvironmentKey() {
    return this.bulkActionForm.get('gatewayEnvironmentKey');
  }

  get tenantId() {
    return this.bulkActionForm.get('tenantId');
  }

  get connectorIds() {
    return this.bulkActionForm.get('connectorIds');
  }

  get groupId() {
    return this.bulkActionForm.get('groupId');
  }
}
